<template>
  <div class="simulation-account-page" v-loading="loading">
    <el-alert
      v-if="errorMessage"
      :title="errorMessage"
      type="error"
      :closable="false"
      show-icon
      class="page-alert"
    >
      <el-button slot="description" size="mini" type="danger" plain @click="refreshData">重新读取</el-button>
    </el-alert>

    <template v-if="overview">
      <el-alert
        v-if="!businessEnabled || !allInvariantsMatch"
        :title="businessEnabled ? '账目校验告警' : accountStatusLabel"
        :description="performanceScopeMessage"
        :type="businessEnabled ? 'warning' : accountStatusType"
        :closable="false"
        show-icon
        class="page-alert"
      />
      <el-card shadow="never" class="account-hero">
        <div class="hero-main">
          <div class="hero-title-row">
            <div>
              <div class="hero-tags">
                <el-tag type="warning" effect="dark" size="small">仅模拟交易</el-tag>
                <el-tag :type="accountStatusType" effect="plain" size="small">{{ accountStatusLabel }}</el-tag>
                <el-tag type="info" effect="plain" size="small">只读账户账本</el-tag>
              </div>
              <h1>{{ account.account_name || '正式模拟账户' }}</h1>
              <p>资金与持仓展示当前账本；正式绩效从确认的启用基线起算。启用前测试记录保留，不计入正式绩效。</p>
            </div>
            <el-button size="small" icon="el-icon-refresh" :loading="loading" @click="refreshData">刷新账户</el-button>
          </div>

          <div class="identity-grid">
            <div class="identity-item wide">
              <span>账户完整编号</span>
              <strong class="id-text">{{ account.account_id }}</strong>
              <el-button type="text" size="mini" @click="copyText(account.account_id)">复制</el-button>
            </div>
            <div class="identity-item">
              <span>开户时间</span>
              <strong>{{ formatDateTime(account.opened_at) }}</strong>
            </div>
            <div class="identity-item">
              <span>数据截至</span>
              <strong>{{ formatDateTime(overview.as_of) }}</strong>
            </div>
            <div class="identity-item wide">
              <span>最新快照完整编号</span>
              <strong class="id-text">{{ latestSnapshot.snapshot_id || '—' }}</strong>
              <el-button v-if="latestSnapshot.snapshot_id" type="text" size="mini" @click="copyText(latestSnapshot.snapshot_id)">复制</el-button>
            </div>
            <div class="identity-item">
              <span>快照交易日</span>
              <strong>{{ latestSnapshot.trade_date || '—' }}</strong>
            </div>
            <div class="identity-item">
              <span>账户版本</span>
              <strong>v{{ account.version_no || 0 }}</strong>
            </div>
          </div>
        </div>
      </el-card>

      <div class="metric-grid">
        <el-card v-for="item in metricCards" :key="item.key" shadow="never" class="metric-card">
          <div class="metric-label">{{ item.label }}</div>
          <div class="metric-value" :class="item.tone">{{ item.value }}</div>
          <div class="metric-hint">{{ item.hint }}</div>
        </el-card>
      </div>

      <el-row :gutter="16" class="chart-risk-row">
        <el-col :xs="24" :lg="16">
          <el-card shadow="never" class="chart-card">
            <div slot="header" class="card-header-row">
              <div>
                <strong>账户绩效曲线</strong>
                <span>从正式基线起算；基线不计入日盈亏或交易日数</span>
              </div>
              <el-radio-group v-model="chartMetric" size="mini" @change="renderChart">
                <el-radio-button label="nav">单位净值</el-radio-button>
                <el-radio-button label="dailyPnl">日盈亏</el-radio-button>
                <el-radio-button label="drawdown">回撤</el-radio-button>
              </el-radio-group>
            </div>
            <div ref="performanceChart" class="performance-chart" />
            <div v-if="!businessEnabled || snapshotMetrics.snapshot_count < 2" class="chart-note">
              {{ chartNote }}
            </div>
          </el-card>
        </el-col>

        <el-col :xs="24" :lg="8">
          <el-card shadow="never" class="risk-card">
            <div slot="header" class="card-header-row compact">
              <div>
                <strong>账户风险摘要</strong>
                <span>当前账本与正式绩效分别展示</span>
              </div>
              <el-tag :type="allInvariantsMatch ? 'success' : 'danger'" size="small" effect="plain">
                {{ allInvariantsMatch ? '账目校验通过' : '存在校验告警' }}
              </el-tag>
            </div>
            <div class="risk-list">
              <div class="risk-row">
                <span>当前账本持仓</span>
                <strong>{{ overview.current_position_count }} 只</strong>
              </div>
              <div class="risk-row">
                <span>现金占总资产</span>
                <strong>{{ cashRatioText }}</strong>
              </div>
              <div class="risk-row">
                <span>当前回撤</span>
                <strong :class="rateTone(drawdown.current_drawdown)">{{ percentText(drawdown.current_drawdown) }}</strong>
              </div>
              <div class="risk-row">
                <span>最大回撤</span>
                <strong :class="rateTone(drawdown.max_drawdown)">{{ percentText(drawdown.max_drawdown) }}</strong>
              </div>
              <div class="risk-row">
                <span>最大单日亏损</span>
                <strong :class="moneyTone(snapshotMetrics.max_single_day_loss)">{{ moneyText(snapshotMetrics.max_single_day_loss) }}</strong>
              </div>
              <div class="risk-row">
                <span>正式日快照</span>
                <strong>{{ snapshotMetrics.snapshot_count || 0 }} 条</strong>
              </div>
              <div class="risk-row">
                <span>账本累计费用（含历史）</span>
                <strong>{{ moneyText(account.total_fees) }}</strong>
              </div>
              <div class="risk-row muted">
                <span>行业集中度</span>
                <strong>待行业快照形成</strong>
              </div>
            </div>
          </el-card>
        </el-col>
      </el-row>

      <el-card shadow="never" class="detail-card">
        <el-tabs v-model="activeTab" @tab-click="handleTabChange">
          <el-tab-pane name="positions">
            <span slot="label">当前持仓 <el-badge :value="overview.current_position_count" type="primary" /></span>
            <el-empty v-if="!currentPositions.length" description="当前账本没有未结束的模拟持仓" />
            <el-table v-else :data="currentPositions" stripe border size="small">
              <el-table-column type="expand">
                <template slot-scope="{ row }">
                  <div class="expand-grid">
                    <div><span>持仓完整编号</span><strong class="id-text">{{ row.sim_id }}</strong></div>
                    <div><span>账户完整编号</span><strong class="id-text">{{ row.account_id }}</strong></div>
                    <div><span>起始日志编号</span><strong class="id-text">{{ row.start_log_id || '—' }}</strong></div>
                    <div><span>最近日志编号</span><strong class="id-text">{{ row.last_log_id || '—' }}</strong></div>
                    <div><span>请求编号</span><strong class="id-text">{{ row.request_id || '—' }}</strong></div>
                    <div><span>策略版本</span><strong class="id-text">{{ row.ruleset_version || '—' }}</strong></div>
                    <div><span>治理版本</span><strong class="id-text">{{ row.governance_ruleset_version || '—' }}</strong></div>
                    <div><span>策略摘要</span><strong>{{ displayValue(row.strategy_summary) }}</strong></div>
                  </div>
                </template>
              </el-table-column>
              <el-table-column prop="ts_code" label="代码" width="110" fixed />
              <el-table-column prop="name" label="名称" width="110" fixed />
              <el-table-column label="状态" width="90">
                <template slot-scope="{ row }"><el-tag :type="positionStatusType(row.status)" size="mini">{{ positionStatusLabel(row.status) }}</el-tag></template>
              </el-table-column>
              <el-table-column label="数量" width="90" align="right">
                <template slot-scope="{ row }">{{ numberText(row.current_qty, 0) }}</template>
              </el-table-column>
              <el-table-column label="平均成本" width="110" align="right">
                <template slot-scope="{ row }">{{ numberText(row.avg_cost, 4) }}</template>
              </el-table-column>
              <el-table-column label="当前价" width="100" align="right">
                <template><el-tooltip content="逐股日快照估值明细尚未形成" placement="top"><span class="muted-value">待逐股快照</span></el-tooltip></template>
              </el-table-column>
              <el-table-column label="持仓市值" width="115" align="right">
                <template><el-tooltip content="账户级总市值可用，逐股市值暂不补造" placement="top"><span class="muted-value">待逐股快照</span></el-tooltip></template>
              </el-table-column>
              <el-table-column label="浮动盈亏" width="115" align="right">
                <template><el-tooltip content="逐股估值快照形成后展示" placement="top"><span class="muted-value">待逐股快照</span></el-tooltip></template>
              </el-table-column>
              <el-table-column label="止损位" width="100" align="right">
                <template slot-scope="{ row }">{{ displayValue(row.stop_loss) }}</template>
              </el-table-column>
              <el-table-column label="止盈位" width="100" align="right">
                <template slot-scope="{ row }">{{ displayValue(row.take_profit) }}</template>
              </el-table-column>
              <el-table-column label="开户时间" min-width="160">
                <template slot-scope="{ row }">{{ formatDateTime(row.opened_at) }}</template>
              </el-table-column>
              <el-table-column label="持仓编号" min-width="290">
                <template slot-scope="{ row }"><span class="id-text">{{ row.sim_id }}</span></template>
              </el-table-column>
            </el-table>
          </el-tab-pane>

          <el-tab-pane name="cycles">
            <span slot="label">交易周期 <el-badge :value="tradePerformance.total_cycle_count || 0" type="success" /></span>
            <el-empty v-if="!cycleRows.length" description="暂无交易周期" />
            <el-table v-else :data="cycleRows" stripe border size="small">
              <el-table-column type="expand">
                <template slot-scope="{ row }">
                  <div class="expand-grid">
                    <div><span>交易周期完整编号</span><strong class="id-text">{{ row.cycle_id }}</strong></div>
                    <div><span>用户覆盖次数</span><strong>{{ row.user_override_count || 0 }}</strong></div>
                    <div><span>总买入金额</span><strong>{{ moneyText(row.gross_buy_amount) }}</strong></div>
                    <div><span>总卖出金额</span><strong>{{ moneyText(row.gross_sell_amount) }}</strong></div>
                    <div><span>周期费用</span><strong>{{ moneyText(row.total_fees) }}</strong></div>
                    <div><span>周期结果</span><strong>{{ cycleOutcomeLabel(row.outcome, row.status) }}</strong></div>
                  </div>
                </template>
              </el-table-column>
              <el-table-column prop="ts_code" label="代码" width="110" />
              <el-table-column prop="name" label="名称" width="110" />
              <el-table-column label="状态" width="90">
                <template slot-scope="{ row }"><el-tag :type="cycleStatusType(row.status)" size="mini">{{ cycleStatusLabel(row.status) }}</el-tag></template>
              </el-table-column>
              <el-table-column label="开仓时间" min-width="160">
                <template slot-scope="{ row }">{{ formatDateTime(row.opened_at) }}</template>
              </el-table-column>
              <el-table-column label="平仓时间" min-width="160">
                <template slot-scope="{ row }">{{ formatDateTime(row.closed_at) }}</template>
              </el-table-column>
              <el-table-column label="持仓天数" width="95" align="right">
                <template slot-scope="{ row }">{{ row.holding_days == null ? '—' : row.holding_days }}</template>
              </el-table-column>
              <el-table-column label="已实现盈亏" width="125" align="right">
                <template slot-scope="{ row }"><span :class="moneyTone(row.net_realized_pnl)">{{ moneyText(row.net_realized_pnl) }}</span></template>
              </el-table-column>
              <el-table-column label="周期收益率" width="115" align="right">
                <template slot-scope="{ row }"><span :class="rateTone(row.return_rate)">{{ percentText(row.return_rate) }}</span></template>
              </el-table-column>
              <el-table-column label="周期完整编号" min-width="300">
                <template slot-scope="{ row }"><span class="id-text">{{ row.cycle_id }}</span></template>
              </el-table-column>
            </el-table>
          </el-tab-pane>

          <el-tab-pane name="events">
            <span slot="label">历史事件 <el-badge :value="recentEvents.length" type="warning" /></span>
            <el-alert title="账户历史事件包含启用前测试记录；保留用于追溯，不代表已计入正式绩效。" type="info" :closable="false" show-icon class="tab-alert" />
            <el-empty v-if="!recentEvents.length" description="暂无账户历史事件" />
            <el-timeline v-else class="event-timeline">
              <el-timeline-item
                v-for="event in recentEvents"
                :key="event.event_id"
                :timestamp="formatDateTime(event.as_of)"
                placement="top"
                :type="eventTimelineType(event.event_type)"
              >
                <el-card shadow="never" class="event-card">
                  <div class="event-title">
                    <strong>{{ eventTypeLabel(event.event_type) }} · {{ event.name || event.ts_code }}</strong>
                    <el-tag v-if="event.user_override" type="warning" size="mini">用户覆盖</el-tag>
                    <el-tag size="mini" effect="plain">{{ event.action_alignment || '—' }}</el-tag>
                  </div>
                  <div class="event-values">
                    <span>数量变化：{{ signedNumber(event.qty_delta) }}</span>
                    <span>成交价：{{ numberText(event.price, 4) }}</span>
                    <span>成交金额：{{ moneyText(event.amount) }}</span>
                    <span>费用：{{ moneyText(event.fee) }}</span>
                    <span>操作后数量：{{ numberText(event.current_qty_after, 0) }}</span>
                    <span>已实现盈亏变化：{{ moneyText(event.realized_pnl_delta) }}</span>
                  </div>
                  <div class="event-ids">
                    <div><span>事件完整编号</span><strong class="id-text">{{ event.event_id }}</strong></div>
                    <div><span>日志完整编号</span><strong class="id-text">{{ event.log_id || '—' }}</strong></div>
                    <div><span>来源日志编号</span><strong class="id-text">{{ event.source_log_id || '—' }}</strong></div>
                    <div><span>请求编号</span><strong class="id-text">{{ event.request_id || '—' }}</strong></div>
                    <div><span>来源结论编号</span><strong class="id-text">{{ event.conclusion_id || '—' }}</strong></div>
                  </div>
                </el-card>
              </el-timeline-item>
            </el-timeline>
          </el-tab-pane>

          <el-tab-pane name="strategy">
            <span slot="label">策略复盘</span>
            <div class="strategy-summary-grid">
              <el-card shadow="never" class="strategy-summary-card">
                <span>当前策略版本</span>
                <strong>{{ strategyVersions.length }} 个</strong>
                <div v-if="strategyVersions.length" class="version-list">
                  <el-tag v-for="version in strategyVersions" :key="version" size="mini" effect="plain">{{ version }}</el-tag>
                </div>
                <p v-else>当前持仓未提供策略版本。</p>
              </el-card>
              <el-card shadow="never" class="strategy-summary-card">
                <span>用户覆盖动作</span>
                <strong>{{ totalUserOverrides }} 次</strong>
                <p>来自正式交易周期，保留与小七原始建议的长期差异。</p>
              </el-card>
              <el-card shadow="never" class="strategy-summary-card">
                <span>已结束有效交易</span>
                <strong>{{ tradePerformance.completed_cycle_count || 0 }} 笔</strong>
                <p>开放持仓不计入主胜率。</p>
              </el-card>
              <el-card shadow="never" class="strategy-summary-card">
                <span>主胜率口径</span>
                <strong>{{ percentText(tradePerformance.win_rate) }}</strong>
                <p>盈利完整交易数 ÷ 已结束有效交易数。</p>
              </el-card>
            </div>

            <div class="gap-grid">
              <el-card v-for="gap in dataGaps" :key="gap.code" shadow="never" class="gap-card">
                <div class="gap-title"><i class="el-icon-info" />{{ gap.label }}</div>
                <p>{{ gap.effect }}</p>
                <el-tag type="info" size="mini" effect="plain">不阻塞阶段 4</el-tag>
              </el-card>
            </div>
          </el-tab-pane>

          <el-tab-pane name="flows">
            <span slot="label">账户流水 <el-badge :value="accountFlowEvents.length" type="primary" /></span>
            <el-alert
              title="账户流水包含启用前测试记录。开户、买入扣款、卖出回款及费用均保留用于追溯；本页面只读，不据此认定业务已启用。"
              type="info"
              :closable="false"
              show-icon
              class="tab-alert"
            />
            <el-empty v-if="!accountFlowEvents.length" description="暂无正式账户资金流水" />
            <el-table v-else :data="accountFlowEvents" stripe border size="small">
              <el-table-column label="时间" min-width="160">
                <template slot-scope="{ row }">{{ formatDateTime(row.as_of) }}</template>
              </el-table-column>
              <el-table-column label="类型" width="110">
                <template slot-scope="{ row }"><el-tag :type="flowType(row.event_type)" size="mini">{{ flowLabel(row.event_type) }}</el-tag></template>
              </el-table-column>
              <el-table-column label="金额" width="125" align="right">
                <template slot-scope="{ row }">{{ moneyText(row.amount) }}</template>
              </el-table-column>
              <el-table-column label="现金变化" width="125" align="right">
                <template slot-scope="{ row }"><span :class="moneyTone(row.cash_delta)">{{ signedMoney(row.cash_delta) }}</span></template>
              </el-table-column>
              <el-table-column label="变动前现金" width="135" align="right">
                <template slot-scope="{ row }">{{ moneyText(row.cash_before) }}</template>
              </el-table-column>
              <el-table-column label="变动后现金" width="135" align="right">
                <template slot-scope="{ row }">{{ moneyText(row.cash_after) }}</template>
              </el-table-column>
              <el-table-column label="关联持仓编号" min-width="290">
                <template slot-scope="{ row }"><span class="id-text">{{ row.related_sim_id || '—' }}</span></template>
              </el-table-column>
              <el-table-column label="资金事件完整编号" min-width="320">
                <template slot-scope="{ row }"><span class="id-text">{{ row.account_event_id }}</span></template>
              </el-table-column>
              <el-table-column label="日志完整编号" min-width="260">
                <template slot-scope="{ row }"><span class="id-text">{{ row.log_id || '—' }}</span></template>
              </el-table-column>
              <el-table-column label="确认差异编号" min-width="260">
                <template slot-scope="{ row }"><span class="id-text">{{ row.diff_id || '—' }}</span></template>
              </el-table-column>
            </el-table>
          </el-tab-pane>
        </el-tabs>
      </el-card>

      <el-alert
        title="阶段 4 安全边界：页面没有买入、卖出或直接改账按钮。后续模拟操作仍必须经过 Preview → Confirm → Execute → Readback。"
        type="warning"
        :closable="false"
        show-icon
        class="safety-alert"
      />
    </template>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import { simulationAccountApi, simulationAccountErrorMessage, simulationAccountDisplayState } from '@/api/simulationAccount'

export default {
  name: 'SimulationAccount',
  data() {
    return {
      loading: false,
      overview: null,
      errorMessage: '',
      activeTab: 'positions',
      chartMetric: 'nav',
      chart: null
    }
  },
  computed: {
    performance() {
      return (this.overview && this.overview.performance) || {}
    },
    account() {
      return this.performance.account || {}
    },
    valuation() {
      return this.performance.current_valuation || {}
    },
    tradePerformance() {
      return this.performance.trade_performance || {}
    },
    snapshotMetrics() {
      return this.performance.daily_snapshot_metrics || {}
    },
    drawdown() {
      return this.snapshotMetrics.drawdown || {}
    },
    latestSnapshot() {
      return this.valuation.latest_confirmed_snapshot || {}
    },
    snapshotRows() {
      return Array.isArray(this.snapshotMetrics.recent_snapshots) ? this.snapshotMetrics.recent_snapshots : []
    },
    currentPositions() {
      return (this.overview && this.overview.current_positions) || []
    },
    cycleRows() {
      return Array.isArray(this.tradePerformance.recent_cycles) ? this.tradePerformance.recent_cycles : []
    },
    recentEvents() {
      return (this.overview && this.overview.recent_events) || []
    },
    accountFlowEvents() {
      return (this.overview && this.overview.account_flow_events) || []
    },
    dataGaps() {
      return (this.overview && this.overview.data_gaps) || []
    },
    allInvariantsMatch() {
      return this.performance.all_invariants_match === true
    },
    businessEnabled() {
      return this.performance.performance_scope && this.performance.performance_scope.business_enabled === true
    },
    performanceScopeMessage() {
      const scope = this.performance.performance_scope || {}
      if (scope.status === 'active') return '正式绩效基线已建立，但当前账目存在校验差异。页面结果需核验，不应作为已通过校验的绩效使用。'
      if (scope.status === 'not_activated') return '系统仍在建设验收中，尚未确认正式绩效起点。当前账本和测试历史可查看；正式收益、净值和回撤暂不计算。'
      if (scope.status === 'unsupported_post_activation_cash_flow') return '正式基线之后发生了资金变动，当前净值算法尚不支持该情况。正式绩效暂不展示，账户历史保留。'
      return '正式基线或后续快照未通过完整性核验。正式绩效暂不展示，账户历史保留，需完成核验后恢复。'
    },
    accountStatusLabel() {
      return simulationAccountDisplayState(this.performance).label
    },
    accountStatusType() {
      return simulationAccountDisplayState(this.performance).type
    },
    chartNote() {
      if (!this.businessEnabled) return this.performanceScopeMessage
      if (!this.snapshotMetrics.snapshot_count) return '正式基线已建立，尚无基线后的日终快照；基线不计入日盈亏或交易日数。'
      return `已有 ${this.snapshotMetrics.snapshot_count} 个基线后的正式日快照，曲线按实际数据形成。`
    },
    cashRatioText() {
      const cash = this.asNumber(this.account.available_cash)
      const assets = this.asNumber(this.valuation.total_assets)
      if (cash == null || assets == null || assets === 0) return '—'
      return this.percentText(cash / assets)
    },
    strategyVersions() {
      const values = this.currentPositions.map(item => item.ruleset_version).filter(Boolean)
      return Array.from(new Set(values))
    },
    totalUserOverrides() {
      return this.cycleRows.reduce((total, item) => total + Number(item.user_override_count || 0), 0)
    },
    metricCards() {
      return [
        { key: 'assets', label: '当前账本总资产', value: this.moneyText(this.valuation.total_assets), hint: '现金＋冻结资金＋持仓市值，包含历史记录', tone: '' },
        { key: 'cash', label: '可用现金', value: this.moneyText(this.account.available_cash), hint: `初始资金 ${this.moneyText(this.account.initial_capital)}`, tone: '' },
        { key: 'market', label: '持仓市值', value: this.moneyText(this.valuation.market_value), hint: `${this.overview.current_position_count || 0} 只未结束持仓`, tone: '' },
        { key: 'pnl', label: '正式累计盈亏', value: this.signedMoney(this.valuation.cumulative_pnl), hint: '当前总资产－正式基线资产－基线后净追加资金', tone: this.moneyTone(this.valuation.cumulative_pnl) },
        { key: 'return', label: '正式累计收益率', value: this.percentText(this.valuation.cumulative_return), hint: '按资金流调整后的单位净值计算，排除测试期收益', tone: this.rateTone(this.valuation.cumulative_return) },
        { key: 'nav', label: '单位净值', value: this.numberText(this.valuation.unit_nav, 8), hint: this.businessEnabled ? '正式启用基线 1.00000000' : '正式绩效当前不可用', tone: this.rateTone(this.valuation.cumulative_return) },
        { key: 'realized', label: '正式已实现盈亏', value: this.signedMoney(this.account.realized_pnl_since_baseline), hint: '正式基线后的已实现盈亏，追加和撤资不计盈亏', tone: this.moneyTone(this.account.realized_pnl_since_baseline) },
        { key: 'unrealized', label: '账本浮动盈亏', value: this.signedMoney(this.valuation.unrealized_pnl), hint: '当前账本估值，包含历史持仓', tone: this.moneyTone(this.valuation.unrealized_pnl) },
        { key: 'winRate', label: '主胜率', value: this.percentText(this.tradePerformance.win_rate), hint: `${this.tradePerformance.completed_cycle_count || 0} 笔已结束有效交易`, tone: '' },
        { key: 'profitFactor', label: '盈亏比', value: this.profitFactorText, hint: this.profitFactorHint, tone: '' },
        { key: 'drawdown', label: '最大回撤', value: this.percentText(this.drawdown.max_drawdown), hint: `${this.snapshotMetrics.snapshot_count || 0} 个日快照`, tone: this.rateTone(this.drawdown.max_drawdown) },
        { key: 'cycles', label: '已结束完整交易', value: `${this.tradePerformance.completed_cycle_count || 0} 笔`, hint: `当前开放周期 ${this.tradePerformance.open_cycle_count || 0} 笔`, tone: '' }
      ]
    },
    profitFactorText() {
      if (this.tradePerformance.profit_factor != null) return this.numberText(this.tradePerformance.profit_factor, 4)
      if (this.tradePerformance.profit_factor_status === 'not_applicable_no_losses') return '暂无亏损样本'
      return '—'
    },
    profitFactorHint() {
      if (this.tradePerformance.profit_factor_status === 'not_applicable_no_losses') return '不能以无亏损样本计算盈亏比'
      return '总盈利 ÷ 总亏损绝对值'
    }
  },
  mounted() {
    this.refreshData()
    window.addEventListener('resize', this.resizeChart)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.resizeChart)
    if (this.chart) {
      this.chart.dispose()
      this.chart = null
    }
  },
  methods: {
    async refreshData() {
      this.loading = true
      this.errorMessage = ''
      try {
        this.overview = await simulationAccountApi.getOverview()
        this.$nextTick(this.renderChart)
      } catch (error) {
        this.overview = null
        this.errorMessage = simulationAccountErrorMessage(error)
      } finally {
        this.loading = false
      }
    },
    handleTabChange() {
      this.$nextTick(this.resizeChart)
    },
    renderChart() {
      if (!this.$refs.performanceChart || !this.overview) return
      if (!this.chart) this.chart = echarts.init(this.$refs.performanceChart)
      const rows = this.snapshotRows
      const labels = rows.map(item => item.trade_date)
      let values = []
      let seriesName = '单位净值'
      let formatter = value => this.numberText(value, 8)
      if (this.chartMetric === 'dailyPnl') {
        values = rows.map(item => this.asNumber(item.daily_pnl))
        seriesName = '日盈亏'
        formatter = value => this.moneyText(value)
      } else if (this.chartMetric === 'drawdown') {
        values = rows.map(item => {
          const value = this.asNumber(item.current_drawdown)
          return value == null ? null : value * 100
        })
        seriesName = '回撤'
        formatter = value => `${this.numberText(value, 4)}%`
      } else {
        values = rows.map(item => this.asNumber(item.unit_nav))
      }
      this.chart.setOption({
        animation: false,
        grid: { left: 64, right: 24, top: 34, bottom: 44 },
        tooltip: {
          trigger: 'axis',
          formatter: params => {
            const point = params && params[0]
            if (!point) return '暂无数据'
            return `${point.axisValue}<br/>${seriesName}：${formatter(point.data)}`
          }
        },
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: labels,
          axisLabel: { color: '#64748b' },
          axisLine: { lineStyle: { color: '#d8e2ef' } }
        },
        yAxis: {
          type: 'value',
          scale: true,
          axisLabel: { color: '#64748b' },
          splitLine: { lineStyle: { color: '#edf2f7' } }
        },
        series: [{
          name: seriesName,
          type: 'line',
          data: values,
          smooth: false,
          connectNulls: false,
          symbolSize: 8,
          showSymbol: true,
          lineStyle: { width: 2 },
          areaStyle: { opacity: 0.08 }
        }],
        graphic: rows.length ? [] : [{
          type: 'text',
          left: 'center',
          top: 'middle',
          style: { text: this.businessEnabled ? '暂无正式日快照' : this.accountStatusLabel, fill: '#94a3b8', fontSize: 14 }
        }]
      }, true)
    },
    resizeChart() {
      if (this.chart) this.chart.resize()
    },
    asNumber(value) {
      if (value == null || value === '') return null
      const parsed = Number(value)
      return Number.isFinite(parsed) ? parsed : null
    },
    moneyText(value) {
      const parsed = this.asNumber(value)
      if (parsed == null) return '—'
      return `${parsed.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} 元`
    },
    signedMoney(value) {
      const parsed = this.asNumber(value)
      if (parsed == null) return '—'
      const prefix = parsed > 0 ? '+' : ''
      return `${prefix}${parsed.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} 元`
    },
    signedNumber(value) {
      const parsed = this.asNumber(value)
      if (parsed == null) return '—'
      return `${parsed > 0 ? '+' : ''}${parsed.toLocaleString('zh-CN')}`
    },
    percentText(value) {
      const parsed = this.asNumber(value)
      if (parsed == null) return '—'
      return `${(parsed * 100).toFixed(4)}%`
    },
    numberText(value, digits) {
      const parsed = this.asNumber(value)
      if (parsed == null) return '—'
      return parsed.toLocaleString('zh-CN', { minimumFractionDigits: digits, maximumFractionDigits: digits })
    },
    displayValue(value) {
      if (value == null || value === '') return '—'
      if (typeof value === 'object') return JSON.stringify(value)
      return String(value)
    },
    formatDateTime(value) {
      if (!value) return '—'
      const text = String(value).replace('T', ' ')
      return text.length > 19 ? text.slice(0, 19) : text
    },
    moneyTone(value) {
      const parsed = this.asNumber(value)
      if (parsed == null || parsed === 0) return ''
      return parsed > 0 ? 'positive' : 'negative'
    },
    rateTone(value) {
      return this.moneyTone(value)
    },
    positionStatusLabel(status) {
      const map = { open: '持仓中', stale: '待复核', closed: '已平仓' }
      return map[status] || status || '未知'
    },
    positionStatusType(status) {
      const map = { open: 'success', stale: 'warning', closed: 'info' }
      return map[status] || 'info'
    },
    cycleStatusLabel(status) {
      return status === 'closed' ? '已结束' : status === 'open' ? '开放中' : status || '未知'
    },
    cycleStatusType(status) {
      return status === 'closed' ? 'success' : status === 'open' ? 'warning' : 'info'
    },
    cycleOutcomeLabel(outcome, status) {
      if (status !== 'closed') return '开放交易，不计主胜率'
      const map = { win: '盈利', loss: '亏损', flat: '平局' }
      return map[outcome] || '待判定'
    },
    eventTypeLabel(type) {
      const map = {
        open: '开仓', add: '加仓', reduce: '减仓', close: '普通平仓',
        take_profit: '止盈', stop_loss: '止损', t1_settlement: 'T+1 可用确认',
        review: '复核', mark_stale: '标记待复核', abandon: '放弃'
      }
      return map[type] || type || '事件'
    },
    eventTimelineType(type) {
      if (['stop_loss', 'mark_stale'].indexOf(type) >= 0) return 'danger'
      if (['close', 'take_profit'].indexOf(type) >= 0) return 'success'
      if (['open', 'add'].indexOf(type) >= 0) return 'primary'
      return 'info'
    },
    flowLabel(type) {
      const map = {
        open_account: '开户', deposit: '追加资金', withdraw: '转出资金',
        buy_debit: '买入扣款', sell_credit: '卖出回款', fee: '费用',
        controlled_adjustment: '受控调整'
      }
      return map[type] || type || '资金事件'
    },
    flowType(type) {
      if (type === 'sell_credit' || type === 'deposit') return 'success'
      if (type === 'buy_debit' || type === 'withdraw' || type === 'fee') return 'warning'
      return 'info'
    },
    async copyText(value) {
      if (!value) return
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(String(value))
        } else {
          const textarea = document.createElement('textarea')
          textarea.value = String(value)
          document.body.appendChild(textarea)
          textarea.select()
          document.execCommand('copy')
          document.body.removeChild(textarea)
        }
        this.$message.success('完整编号已复制')
      } catch (error) {
        this.$message.warning('复制失败，请手动选择编号')
      }
    }
  }
}
</script>

<style scoped>
.simulation-account-page { padding-bottom: 28px; }
.page-alert { margin-bottom: 16px; }
.account-hero { margin-bottom: 16px; border: 1px solid #cbdcf4; background: linear-gradient(135deg, #f8fbff 0%, #eef5ff 100%); }
.account-hero >>> .el-card__body { padding: 22px 24px; }
.hero-title-row { display: flex; justify-content: space-between; align-items: flex-start; gap: 18px; }
.hero-tags { display: flex; gap: 8px; flex-wrap: wrap; }
.hero-title-row h1 { margin: 12px 0 6px; color: #0f172a; font-size: 26px; }
.hero-title-row p { margin: 0; color: #64748b; line-height: 1.7; }
.identity-grid { margin-top: 20px; display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 10px; }
.identity-item { min-width: 0; padding: 11px 13px; border-radius: 11px; background: rgba(255,255,255,.78); border: 1px solid #dbe7f5; }
.identity-item.wide { grid-column: span 2; }
.identity-item span, .identity-item strong { display: block; }
.identity-item span { color: #64748b; font-size: 12px; }
.identity-item strong { margin-top: 5px; color: #1e293b; font-size: 13px; }
.identity-item .el-button { padding: 4px 0 0; }
.id-text { font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace; word-break: break-all; font-weight: 600; }
.metric-grid { display: grid; grid-template-columns: repeat(6, minmax(0, 1fr)); gap: 12px; margin-bottom: 16px; }
.metric-card >>> .el-card__body { padding: 16px; }
.metric-label { color: #64748b; font-size: 12px; }
.metric-value { margin-top: 9px; color: #0f172a; font-size: 20px; font-weight: 800; white-space: nowrap; }
.metric-hint { min-height: 34px; margin-top: 7px; color: #94a3b8; font-size: 11px; line-height: 1.5; }
.positive { color: #dc2626 !important; }
.negative { color: #059669 !important; }
.chart-risk-row { margin-bottom: 16px; }
.chart-card, .risk-card { min-height: 410px; }
.card-header-row { display: flex; align-items: center; justify-content: space-between; gap: 14px; }
.card-header-row > div:first-child strong, .card-header-row > div:first-child span { display: block; }
.card-header-row > div:first-child strong { color: #0f172a; font-size: 15px; }
.card-header-row > div:first-child span { margin-top: 4px; color: #94a3b8; font-size: 12px; }
.card-header-row.compact { align-items: flex-start; }
.performance-chart { height: 300px; }
.chart-note { padding: 8px 12px; border-radius: 8px; background: #f8fafc; color: #64748b; font-size: 12px; text-align: center; }
.risk-list { display: flex; flex-direction: column; }
.risk-row { min-height: 42px; display: flex; align-items: center; justify-content: space-between; gap: 12px; border-bottom: 1px solid #edf2f7; }
.risk-row:last-child { border-bottom: 0; }
.risk-row span { color: #64748b; font-size: 13px; }
.risk-row strong { color: #1e293b; font-size: 13px; text-align: right; }
.risk-row.muted strong { color: #94a3b8; font-weight: 500; }
.detail-card { margin-bottom: 16px; }
.expand-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px 18px; padding: 8px 24px; }
.expand-grid > div, .event-ids > div { min-width: 0; }
.expand-grid span, .expand-grid strong, .event-ids span, .event-ids strong { display: block; }
.expand-grid span, .event-ids span { color: #94a3b8; font-size: 11px; }
.expand-grid strong, .event-ids strong { margin-top: 4px; color: #334155; font-size: 12px; }
.muted-value { color: #94a3b8; font-size: 11px; white-space: nowrap; }
.event-timeline { padding: 12px 14px 0; }
.event-card { border-color: #e2e8f0; }
.event-title { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.event-title strong { margin-right: auto; color: #0f172a; }
.event-values { margin-top: 12px; display: flex; gap: 18px; flex-wrap: wrap; color: #475569; font-size: 12px; }
.event-ids { margin-top: 13px; display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px 18px; padding-top: 12px; border-top: 1px dashed #e2e8f0; }
.strategy-summary-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 12px; margin: 8px 0 16px; }
.strategy-summary-card span, .strategy-summary-card strong { display: block; }
.strategy-summary-card span { color: #64748b; font-size: 12px; }
.strategy-summary-card strong { margin-top: 8px; color: #0f172a; font-size: 22px; }
.strategy-summary-card p { margin: 8px 0 0; color: #94a3b8; font-size: 12px; line-height: 1.6; }
.version-list { margin-top: 9px; display: flex; gap: 6px; flex-wrap: wrap; }
.gap-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; }
.gap-card { background: #f8fafc; }
.gap-title { color: #334155; font-weight: 700; }
.gap-title i { margin-right: 7px; color: #2563eb; }
.gap-card p { min-height: 38px; color: #64748b; font-size: 12px; line-height: 1.7; }
.tab-alert { margin: 4px 0 14px; }
.safety-alert { margin-top: 16px; }

@media (max-width: 1500px) {
  .metric-grid { grid-template-columns: repeat(4, minmax(0, 1fr)); }
  .identity-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
}
@media (max-width: 1100px) {
  .metric-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
  .identity-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .strategy-summary-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
@media (max-width: 760px) {
  .hero-title-row { flex-direction: column; }
  .identity-grid, .metric-grid, .strategy-summary-grid, .gap-grid, .expand-grid, .event-ids { grid-template-columns: 1fr; }
  .identity-item.wide { grid-column: span 1; }
  .card-header-row { align-items: flex-start; flex-direction: column; }
}
</style>
